// src/redux/slices/clinicalChartModalSlice.ts

import { pdf } from "@react-pdf/renderer";
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import dayjs from "dayjs";
import { clinical_chart, csl, IAppointment, labs, vitals } from "lobbie";
import { PDFDocument } from "pdf-lib";
import { pdfjs } from "react-pdf";
import {
    AllergyProfile,
    KnownStatus,
    MedsProfile,
    ProblemsProfile,
} from "src/components/shared/patients/health_profile/healthProfileSchema";
import { MONTH_DAY_YEAR_TIME_FORMAT } from "src/constants";
import { BASE_API_URL, BASE_SECURE_ROUTE } from "src/constants/api";
import { handleBackendHttpError } from "src/redux/handleBackendHttpError";
import { RootState } from "src/redux/store";
import { LabVendor } from "src/schemas/settings/labs/LabVendor";
import { EChartStatus, ESearchTableType } from "src/types/global_enums";
import { LabMachineModel } from "src/types/models/labs-configuration";
import { handleError, notify } from "src/utils";
import { deleteUploadedFile, uploadFile } from "src/utils/aws";
import {
    addBloodPressureRow,
    addHeightandBMIRow,
    formatTableData,
    sortVitals,
} from "src/utils/print_table";
import { initialAllergyProfileState } from "./healthProfileAllergySlice";
import { triggerSearchTableRefetch } from "./searchTableRefetchSlice";

// https://github.com/wojtekmaj/react-pdf?tab=readme-ov-file#legacy-pdfjs-worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/legacy/build/pdf.worker.min.mjs`;

interface PDFTableData {
    title: string;
    headers: string[];
    rows: any[]; // Use the appropriate type from your print_table.ts
}

// interface to store all chart-related data
interface ChartData {
    appointment: IAppointment;
    labTrends?: labs.ILabTrendsView;
    vitalsTrends?: vitals.IVitalsTrendsView;
    appointmentLabs: labs.ILab[];
    labVendors: any[];
    labMachines?: any[];
    labsTableData?: PDFTableData;
    vitalsTableData?: PDFTableData;
    labsPrintProps?: any[];
    logoUrl?: string;
    allergyProfile: AllergyProfile;
    problemsProfile: ProblemsProfile;
    medicationsProfile: MedsProfile;
    addenda?: clinical_chart.IAddendum[];
    signatureRequest?: csl.ISignatureRequest;
}

interface IClinicalChartModalState {
    isOpen: boolean;
    isLoading: boolean;
    error: string | null;
    chart: clinical_chart.IClinicalChartSearchRow | null;
    pdfUrl: string | null;
    loadingProgress: number;
    scale: number;
    canUnlock: boolean;
    chartData: ChartData | null;
    executingActionForAppointmentId: number | undefined;
}

interface IPDFComponents {
    ClinicalChartAppointment: any;
    SignatureCertificatePage: any;
    ClinicalChartAddendumPage: any;
}

export const initialStateClinicalChartModal: IClinicalChartModalState = {
    isOpen: false,
    isLoading: false,
    error: null,
    chart: null,
    pdfUrl: null,
    loadingProgress: 0,
    scale: 0.8,
    canUnlock: false,
    chartData: null,
    executingActionForAppointmentId: undefined,
};

export const generateBasePDF = async (
    ClinicalChartAppointment: any,
    chartData: ChartData,
    isFinal: boolean,
    signatureText?: string,
): Promise<Blob> => {
    return await pdf(
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-argument
        ClinicalChartAppointment({
            appointment: chartData.appointment,
            logoUrl: chartData.logoUrl,
            labsTableData: chartData.labsTableData,
            vitalsTableData: chartData.vitalsTableData,
            isFinal,
            labsPrintProps: chartData.labsPrintProps,
            allergyProfile: chartData.allergyProfile,
            problemsProfile: chartData.problemsProfile,
            medicationsProfile: chartData.medicationsProfile,
            signatureText,
        }),
    ).toBlob();
};

interface IPAddressResponse {
    ipAddress: string;
}

export const generateSignatureCertificatePDF = async (
    SignatureCertificatePage: any,
    signatureBlob: string,
    signer: csl.ISignatureRequestSigner,
    appointment: IAppointment,
    originalSignedDateTime?: string, // Add optional parameter for original timestamp
): Promise<Blob> => {
    // Use original timestamp if provided, otherwise use current time (for new signatures)
    const signedDateTime = originalSignedDateTime || new Date().toISOString();
    // Fetch IP address first
    let ipAddress = "unknown";
    try {
        const ipResponse = await axios.get<IPAddressResponse>(
            `${BASE_API_URL}/${BASE_SECURE_ROUTE}/staff/network/ip`,
            { withCredentials: true },
        );
        ipAddress = ipResponse.data.ipAddress || "unknown";
    } catch (error) {
        handleError(error as Error);
    }
    return await pdf(
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-argument
        SignatureCertificatePage({
            signatureBlob,
            signer,
            signedDateTimeISO: signedDateTime,
            ipAddress,
            timezone: appointment.timeZone,
        }),
    ).toBlob();
};
export const generateAddendaPDF = async (
    ClinicalChartAddendumPage: any,
    chartData: ChartData,
    signatureText?: string,
): Promise<Blob | null> => {
    if (!chartData.addenda || chartData.addenda.length === 0) {
        return null;
    }
    return await pdf(
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-argument
        ClinicalChartAddendumPage({
            accountPatient: chartData.appointment.accountPatient,
            practitioner: chartData.appointment.practitioner,
            patientChartId: `${chartData.appointment.accountPatient.id}-${chartData.appointment.id}`,
            logoUrl: chartData.logoUrl,
            timezone: chartData.appointment.timeZone,
            apptStartUnix: chartData.appointment.startUnix,
            addenda: chartData.addenda,
            signatureText,
        }),
    ).toBlob();
};

export const mergePDFs = async (pdfBlobs: Blob[]): Promise<Blob> => {
    const mergedPdf = await PDFDocument.create();

    for (const blob of pdfBlobs) {
        const arrayBuffer = await blob.arrayBuffer();
        const pdfDoc = await PDFDocument.load(arrayBuffer);
        const pages = await mergedPdf.copyPages(pdfDoc, pdfDoc.getPageIndices());
        pages.forEach((page) => mergedPdf.addPage(page));
    }

    return new Blob([await mergedPdf.save()], { type: "application/pdf" });
};

export const generateCompletePDF = async (
    clinicalChartPdfComponents: IPDFComponents,
    chartData: ChartData,
    options: {
        isFinal: boolean;
        includeSignature?: boolean;
        signatureBlob?: string;
        signer?: csl.ISignatureRequestSigner;
        signatureText?: string;
        originalSignedDateTime?: string; // Add this parameter
    },
): Promise<Blob> => {
    const {
        isFinal,
        includeSignature,
        signatureBlob,
        signer,
        signatureText,
        originalSignedDateTime,
    } = options;
    const pdfParts: Blob[] = [];

    // 1. Generate base PDF
    const basePdf = await generateBasePDF(
        clinicalChartPdfComponents.ClinicalChartAppointment,
        chartData,
        isFinal,
        signatureText,
    );
    pdfParts.push(basePdf);

    // 2. Generate signature certificate PDF (if needed)
    if (includeSignature && signatureBlob && signer) {
        const signaturePdf = await generateSignatureCertificatePDF(
            clinicalChartPdfComponents.SignatureCertificatePage,
            signatureBlob,
            signer,
            chartData.appointment,
            originalSignedDateTime, // Pass the original timestamp
        );
        pdfParts.push(signaturePdf);
    }
    // 3. Generate addenda PDF (if any exist)
    const addendaPdf = await generateAddendaPDF(
        clinicalChartPdfComponents.ClinicalChartAddendumPage,
        chartData,
        signatureText,
    );
    if (addendaPdf) {
        pdfParts.push(addendaPdf);
    }
    // 4. Merge all PDFs if there are multiple parts
    if (pdfParts.length === 1) {
        return pdfParts[0];
    }
    return await mergePDFs(pdfParts);
};

const uploadChartPDF = async (
    blob: Blob,
    fileName: string,
    accountId: string,
): Promise<{ id: number; s3ObjectPath: string } | null> => {
    const upload = await uploadFile({ data: blob, name: fileName }, accountId, {
        onError: (error) => {
            handleBackendHttpError(error);
            notify({
                level: "error",
                title: "Error",
                message: "Failed to upload chart file",
            });
        },
    });
    if (!upload) {
        return null;
    }
    return {
        id: upload.id,
        s3ObjectPath: upload.s3ObjectPath,
    };
};

export const fetchChartData = async (
    appointmentId: number,
    state: RootState,
): Promise<ChartData> => {
    // Get branding from Redux state
    const location = state.location?.location;
    const account = state.account?.account;
    // Determine logo URL from existing state
    let logoUrl: string | undefined = undefined;
    if (location?.branding?.logoS3Url) {
        logoUrl = location.branding.logoS3Url;
    } else if (account?.branding?.logoS3Url) {
        logoUrl = account.branding.logoS3Url;
    }
    // Fetch appointment data
    const appointmentResponse = await axios.get(
        `${BASE_API_URL}/${BASE_SECURE_ROUTE}/staff/appointments/${appointmentId}`,
        { withCredentials: true },
    );
    const appointment = appointmentResponse.data as IAppointment;
    if (!appointment) {
        throw new Error("Appointment not found");
    }

    // Fetch clinical chart data separately to get the clinical chart ID
    const clinicalChartResponse = await axios.get<clinical_chart.IClinicalChartSearchRow>(
        `${BASE_API_URL}/${BASE_SECURE_ROUTE}/staff/appointments/clinical-chart/${appointmentId}`,
        { withCredentials: true },
    );
    const clinicalChartId = clinicalChartResponse.data ? clinicalChartResponse.data.id : undefined;
    // Use appointment's account patient ID
    const patientId = appointment.accountPatient.id;
    // Parallel fetching of all required data
    const [
        labTrendsResponse,
        vitalsTrendsResponse,
        appointmentLabsResponse,
        labVendorsResponse,
        labMachinesResponse,
        allergiesResponse,
        problemsResponse,
        medicationsResponse,
        addendaResponse,
        signatureRequestResponse,
    ] = await Promise.all([
        // Lab trends
        axios
            .get<labs.ILabTrendsView>(
                `${BASE_API_URL}/${BASE_SECURE_ROUTE}/staff/labs/trends?accountPatientId=${patientId}&limit=5`,
                { withCredentials: true },
            )
            .catch(() => ({ data: undefined })),
        // Vitals trends
        axios
            .get<vitals.IVitalsTrendsView>(
                `${BASE_API_URL}/${BASE_SECURE_ROUTE}/staff/appointments/${appointmentId}/vitals_trends`,
                { withCredentials: true },
            )
            .catch(() => ({ data: undefined })),
        // Appointment labs
        axios
            .get<labs.ILab[]>(
                `${BASE_API_URL}/${BASE_SECURE_ROUTE}/staff/appointments/${appointmentId}/labs`,
                {
                    withCredentials: true,
                },
            )
            .catch(() => ({ data: [] })),
        // Lab vendors
        axios
            .get<LabVendor[]>(`${BASE_API_URL}/${BASE_SECURE_ROUTE}/staff/labs/vendors`, {
                withCredentials: true,
            })
            .catch(() => ({ data: [] })),
        // Lab machines
        axios
            .get<LabMachineModel[]>(
                `${BASE_API_URL}/${BASE_SECURE_ROUTE}/staff/labs/settings/machines`,
                {
                    withCredentials: true,
                },
            )
            .catch(() => ({ data: [] })),
        // Allergies
        axios
            .get<AllergyProfile>(
                `${BASE_API_URL}/${BASE_SECURE_ROUTE}/staff/account_patients/health-profile/allergies?accountPatientId=${patientId}`,
                {
                    withCredentials: true,
                },
            )
            .catch(() => ({ data: { ...initialAllergyProfileState } })),
        // Problems
        axios
            .get<ProblemsProfile>(
                `${BASE_API_URL}/${BASE_SECURE_ROUTE}/staff/account_patients/health-profile/problems?accountPatientId=${patientId}`,
                {
                    withCredentials: true,
                },
            )
            .catch(() => ({
                data: {
                    problems: [],
                    problemKnownStatus: KnownStatus.UNKNOWN,
                    patientFullName: "",
                },
            })),
        // Medications
        axios
            .get<MedsProfile>(
                `${BASE_API_URL}/${BASE_SECURE_ROUTE}/staff/account_patients/health-profile/medications?accountPatientId=${patientId}`,
                {
                    withCredentials: true,
                },
            )
            .catch(() => ({
                data: {
                    medications: [],
                    medsKnownStatus: KnownStatus.UNKNOWN,
                    patientFullName: "",
                },
            })),
        // Addenda
        clinicalChartId
            ? axios
                  .get<clinical_chart.IAddendum[]>(
                      `${BASE_API_URL}/${BASE_SECURE_ROUTE}/staff/appointments/clinical-chart/addendum?clinicalChartId=${clinicalChartId}`,
                      {
                          withCredentials: true,
                      },
                  )
                  .catch(() => ({ data: [] }))
            : Promise.resolve({ data: [] }),
        // Signature Request
        clinicalChartId
            ? axios
                  .get<csl.ISignatureRequest>(
                      `${BASE_API_URL}/${BASE_SECURE_ROUTE}/staff/appointments/clinical-chart/signature-request?clinicalChartId=${clinicalChartId}`,
                      {
                          withCredentials: true,
                      },
                  )
                  .catch(() => ({ data: undefined }))
            : Promise.resolve({ data: undefined }),
    ]);
    const labTrends = labTrendsResponse.data;
    const vitalsTrends = vitalsTrendsResponse.data;
    const appointmentLabs = appointmentLabsResponse.data || [];
    const labVendors = labVendorsResponse.data || [];
    const labMachines = labMachinesResponse.data || [];
    const allergyProfile = allergiesResponse.data;
    const problemsProfile = problemsResponse.data;
    const medicationsProfile = medicationsResponse.data;
    const addenda = addendaResponse.data;
    const signatureRequest = signatureRequestResponse.data;
    // Process lab data for printing
    const labsPrintProps = appointmentLabs
        .filter((lab) => labVendors?.find((lv) => lv.vendorType === lab.labVendorType))
        .map((lab) => {
            const labVendor = labVendors?.find((lv) => lv.vendorType === lab.labVendorType);
            const labMachine = labMachines?.find((lm) => lm.id === lab.labMachineId);
            return { lab, labVendor, labMachine };
        });
    // Process table data
    let labsTableData;
    if (labTrends) {
        labsTableData = formatTableData("Labs History", labTrends);
    }
    let vitalsTableData;
    if (vitalsTrends) {
        vitalsTableData = formatTableData("Vitals", vitalsTrends);
        if (vitalsTableData) {
            addBloodPressureRow(vitalsTableData);
            addHeightandBMIRow(vitalsTableData);
            sortVitals(vitalsTableData);
        }
    }
    // Return the complete chart data
    return {
        appointment,
        labTrends,
        vitalsTrends,
        appointmentLabs,
        labVendors,
        labMachines,
        labsTableData,
        vitalsTableData,
        labsPrintProps,
        logoUrl,
        allergyProfile,
        problemsProfile,
        medicationsProfile,
        addenda,
        signatureRequest,
    };
};

export const openChartModal = createAsyncThunk<
    ChartData | null,
    { chart: clinical_chart.IClinicalChartSearchRow; canUnlock: boolean },
    { state: RootState; rejectValue: string }
>("clinicalChartModal/openChartModal", async ({ chart }, { getState, rejectWithValue }) => {
    try {
        // If chart has a signedURL, we don't need to fetch all the data
        // We'll just use the existing PDF
        if (chart.signedURL) {
            return null; // No need to fetch chart data
        }
        // Only fetch chart data for draft charts or charts without signed URLs
        return await fetchChartData(chart.appointmentId, getState());
    } catch (error: unknown) {
        handleBackendHttpError(error);
        const errorMessage = error instanceof Error ? error.message : "An unknown error occurred";
        return rejectWithValue(errorMessage);
    }
});

export const downloadChart = createAsyncThunk<
    void,
    {
        chart: clinical_chart.IClinicalChartSearchRow;
        clinicalChartPdfComponents: IPDFComponents;
    },
    { state: RootState; rejectValue: string }
>(
    "clinicalChartModal/downloadChart",
    async ({ chart, clinicalChartPdfComponents }, { getState, rejectWithValue }) => {
        try {
            // Check if chart is finalized/signed AND has a valid signed URL
            const isFinalizedChart =
                chart.chartStatus !== EChartStatus.DRAFT &&
                chart.chartStatus !== EChartStatus.BACK_TO_PROVIDER;
            // there's an edge case where the chart is finalized but the signed URL is empty due to the fileUpload getting deleted or corrupted so we need to generate a new one
            if (isFinalizedChart && chart.signedURL && chart.signedURL.trim() !== "") {
                // Try to open the existing signed URL
                window.open(chart.signedURL, "_blank");
                return;
            }
            // If no valid signed URL exists, generate the PDF
            const chartData = await fetchChartData(chart.appointmentId, getState());
            // Determine if this should be marked as final based on chart status
            const isFinal = isFinalizedChart;
            // For signed charts, include signature information if available
            let includeSignature = false;
            let signatureBlob: string | undefined;
            let signer: csl.ISignatureRequestSigner | undefined;
            let signatureText: string | undefined;
            let originalSignedDateTime: string | undefined;
            if (
                chart.chartStatus === EChartStatus.SIGNED_OFF &&
                chartData.signatureRequest?.completedOnISO
            ) {
                includeSignature = true;
                signatureBlob = chartData.signatureRequest.signatureBlob;
                signer = {
                    id: chartData.signatureRequest.signer.id,
                    firstName: chartData.signatureRequest.signer.firstName,
                    lastName: chartData.signatureRequest.signer.lastName,
                    position: chartData.signatureRequest.signer.position,
                    title: chartData.signatureRequest.signer.title,
                };
                signatureText = `${signer.firstName} ${signer.lastName}${signer.position ? ", " + signer.position : ""}`;
                originalSignedDateTime = chartData.signatureRequest.completedOnISO;
            }
            const blob = await generateCompletePDF(clinicalChartPdfComponents, chartData, {
                isFinal,
                includeSignature,
                signatureBlob,
                signer,
                signatureText,
                originalSignedDateTime,
            });
            const url = URL.createObjectURL(blob);
            window.open(url, "_blank");
            setTimeout(() => URL.revokeObjectURL(url), 5000);
        } catch (error: unknown) {
            handleBackendHttpError(error);
            notify({
                level: "error",
                title: "Error",
                message: error instanceof Error ? error.message : "Failed to download chart",
            });
            return rejectWithValue(
                error instanceof Error ? error.message : "Failed to download chart",
            );
        }
    },
);

export const finalizeChart = createAsyncThunk<
    clinical_chart.IClinicalChartFull,
    {
        appointmentId: number;
        signatureText: string;
        clinicalChartPdfComponents: IPDFComponents;
        chartFileUploadId?: number;
    },
    { state: RootState; rejectValue: string }
>(
    "clinicalChartModal/finalizeChart",
    async (
        { appointmentId, signatureText, clinicalChartPdfComponents },
        { getState, rejectWithValue, dispatch },
    ) => {
        try {
            const state = getState();
            const accountId = state.account?.account?.id;
            if (!accountId) {
                return rejectWithValue("Account ID not found");
            }
            const chartData = await fetchChartData(appointmentId, getState());
            // Generate finalized PDF with signature
            const finalizedBlob = await generateCompletePDF(clinicalChartPdfComponents, chartData, {
                isFinal: true,
                signatureText,
            });
            // Upload the finalized PDF
            const now = dayjs();
            const fileName = `chart-finalized-${now.format(MONTH_DAY_YEAR_TIME_FORMAT)}.pdf`;
            const fileUpload = await uploadFile(
                { data: finalizedBlob, name: fileName },
                accountId.toString(),
                {
                    onError: (error) => {
                        handleBackendHttpError(error);
                        notify({
                            level: "error",
                            title: "Error",
                            message: "Failed to upload finalized chart",
                        });
                    },
                },
            );
            if (!fileUpload) {
                return rejectWithValue("Failed to upload finalized chart");
            }
            const endpoint = `/staff/appointments/clinical-chart/${appointmentId}/finalize_chart/${fileUpload.id}`;
            const response = await axios.put<clinical_chart.IClinicalChartFull>(
                `${BASE_API_URL}/${BASE_SECURE_ROUTE}${endpoint}`,
                { fileUploadId: fileUpload.id },
                { withCredentials: true },
            );
            dispatch(triggerSearchTableRefetch([ESearchTableType.CLINICAL_CHARTS_DASHBOARD]));
            return response.data;
        } catch (error: any) {
            handleBackendHttpError(error);
            return rejectWithValue(
                error instanceof Error ? error.message : "Failed to finalize chart",
            );
        }
    },
);

export const signChart = createAsyncThunk<
    void,
    {
        appointmentId: number;
        signatureBlob: string;
        staffName: string;
        signer: csl.ISignatureRequestSigner;
        clinicalChartPdfComponents: IPDFComponents;
    },
    { state: RootState; rejectValue: string }
>(
    "clinicalChartModal/signChart",
    async (
        { appointmentId, signatureBlob, staffName, signer, clinicalChartPdfComponents },
        { dispatch, getState, rejectWithValue },
    ) => {
        try {
            const state = getState();
            const accountId = state.account?.account?.id;
            if (!accountId) {
                return rejectWithValue("Account ID not found");
            }
            // Fetch chart data if not available in state
            let chartData = state.clinicalChartModal.chartData;
            if (!chartData) {
                chartData = await fetchChartData(appointmentId, getState());
            }
            const signedBlob = await generateCompletePDF(clinicalChartPdfComponents, chartData, {
                isFinal: true,
                includeSignature: true,
                signatureBlob,
                signer,
                signatureText: `${staffName}, ${signer.position || ""}`,
            });
            const now = dayjs();
            const fileName = `chart-signed-${now.format(MONTH_DAY_YEAR_TIME_FORMAT)}.pdf`;
            const fileUpload = await uploadChartPDF(signedBlob, fileName, accountId.toString());
            if (!fileUpload) {
                return rejectWithValue("Failed to upload signed chart");
            }
            try {
                await axios.post(
                    `${BASE_API_URL}/${BASE_SECURE_ROUTE}/staff/appointments/clinical-chart/sign`,
                    {
                        appointmentId,
                        signatureBlob: signatureBlob,
                        fileUploadId: fileUpload.id,
                    },
                    { withCredentials: true },
                );
                dispatch(triggerSearchTableRefetch([ESearchTableType.CLINICAL_CHARTS_DASHBOARD]));
            } catch (error) {
                await deleteUploadedFile(fileUpload.s3ObjectPath);
                handleBackendHttpError(error);
                return rejectWithValue("Failed to sign chart");
            }
        } catch (error: unknown) {
            handleBackendHttpError(error);
            return rejectWithValue(error instanceof Error ? error.message : "Failed to sign chart");
        }
    },
);

export const unlockChart = createAsyncThunk<
    void,
    { appointmentId: number },
    { state: RootState; rejectValue: string }
>("clinicalChartModal/unlockChart", async ({ appointmentId }, { rejectWithValue, dispatch }) => {
    try {
        await axios.put(
            `${BASE_API_URL}/${BASE_SECURE_ROUTE}/staff/appointments/clinical-chart/${appointmentId}/unlock`,
            {},
            { withCredentials: true },
        );
        dispatch(triggerSearchTableRefetch([ESearchTableType.CLINICAL_CHARTS_DASHBOARD]));
    } catch (error: any) {
        handleBackendHttpError(error);
        return rejectWithValue(error instanceof Error ? error.message : "Failed to unlock chart");
    }
});

export const sendToSupervisor = createAsyncThunk<
    void,
    { appointmentId: number },
    { state: RootState; rejectValue: string }
>(
    "clinicalChartModal/sendToSupervisor",
    async ({ appointmentId }, { rejectWithValue, dispatch }) => {
        try {
            await axios.put(
                `${BASE_API_URL}/${BASE_SECURE_ROUTE}/staff/appointments/clinical-chart/send-to-supervisor`,
                { appointmentId },
                { withCredentials: true },
            );
            dispatch(triggerSearchTableRefetch([ESearchTableType.CLINICAL_CHARTS_DASHBOARD]));
            notify({
                level: "success",
                title: "Success",
                message: "Chart sent to supervisor",
            });
        } catch (error: any) {
            handleBackendHttpError(error);
            return rejectWithValue(
                error instanceof Error ? error.message : "Failed to send to supervisor",
            );
        }
    },
);

interface AddAddendumRequest {
    appointmentId: number;
    content: string;
    clinicalChartPdfComponents: IPDFComponents;
}

export const addAddendumAndUpdateChart = createAsyncThunk<
    void,
    AddAddendumRequest,
    { state: RootState; rejectValue: string }
>(
    "clinicalChartModal/addAddendumAndUpdateChart",
    async (
        { appointmentId, content, clinicalChartPdfComponents },
        { dispatch, getState, rejectWithValue },
    ) => {
        try {
            const state = getState();
            const accountId = state.account?.account?.id;
            if (!accountId) {
                return rejectWithValue("Account ID not found");
            }
            // Step 1: Add the addendum
            const newAddendum = await axios
                .post<clinical_chart.IAddendum>(
                    `${BASE_API_URL}/${BASE_SECURE_ROUTE}/staff/appointments/clinical-chart/addendum`,
                    { appointmentId, content },
                    { withCredentials: true },
                )
                .then((response) => response.data);
            // Step 2: Fetch updated chart data (this now includes signature request data)
            const updatedChartData = await fetchChartData(appointmentId, getState());
            if (!updatedChartData) {
                return rejectWithValue("Failed to fetch updated chart data");
            }
            // Step 3: Check if chart is signed and prepare signature data
            const isChartSigned = updatedChartData.signatureRequest?.completedOnISO;
            let includeSignature = false;
            let signatureBlob: string | undefined;
            let signer: csl.ISignatureRequestSigner | undefined;
            if (isChartSigned && updatedChartData.signatureRequest) {
                includeSignature = true;
                signatureBlob = updatedChartData.signatureRequest.signatureBlob;
                signer = {
                    id: updatedChartData.signatureRequest.signer.id,
                    firstName: updatedChartData.signatureRequest.signer.firstName,
                    lastName: updatedChartData.signatureRequest.signer.lastName,
                    position: updatedChartData.signatureRequest.signer.position,
                    title: updatedChartData.signatureRequest.signer.title,
                };
            }
            // Step 4: Generate complete PDF with proper ordering
            const finalBlob = await generateCompletePDF(
                clinicalChartPdfComponents,
                updatedChartData,
                {
                    isFinal: true,
                    includeSignature,
                    signatureBlob,
                    signer,
                    signatureText:
                        includeSignature && signer?.position
                            ? `${signer.firstName} ${signer.lastName}, ${signer.position}`
                            : undefined,
                    originalSignedDateTime: updatedChartData.signatureRequest?.completedOnISO,
                },
            );
            // Step 5: Upload the new PDF file
            const now = dayjs();
            const fileName = includeSignature
                ? `chart-with-addendum-signed-${now.format(MONTH_DAY_YEAR_TIME_FORMAT)}.pdf`
                : `chart-with-addendum-${now.format(MONTH_DAY_YEAR_TIME_FORMAT)}.pdf`;
            const fileUpload = await uploadChartPDF(finalBlob, fileName, accountId.toString());
            if (!fileUpload) {
                return rejectWithValue("Failed to upload updated chart file");
            }
            // Step 6: Update the chart's file upload reference
            await axios.put(
                `${BASE_API_URL}/${BASE_SECURE_ROUTE}/staff/appointments/clinical-chart/update-chart-file-upload`,
                { appointmentId, fileUploadId: fileUpload.id },
                { withCredentials: true },
            );
            notify({
                level: "success",
                title: "Success",
                message: "Addendum added and chart updated successfully",
            });
            dispatch(triggerSearchTableRefetch([ESearchTableType.CLINICAL_CHARTS_DASHBOARD]));
        } catch (error: unknown) {
            handleBackendHttpError(error);
            return rejectWithValue(
                error instanceof Error ? error.message : "Failed to add addendum and update chart",
            );
        }
    },
);

export const regeneratePDFFromCurrentState = createAsyncThunk<
    { url: string },
    {
        appointmentId: number;
        clinicalChartPdfComponents: IPDFComponents;
        chartStatus?: EChartStatus; // Add optional chart status parameter
    },
    { state: RootState; rejectValue: string }
>(
    "clinicalChartModal/regeneratePDFFromCurrentState",
    async (
        { appointmentId, clinicalChartPdfComponents, chartStatus },
        { getState, rejectWithValue, dispatch },
    ) => {
        try {
            const state = getState();
            const accountId = state.account?.account?.id;
            if (!accountId) {
                return rejectWithValue("Account ID not found");
            }
            // Fetch fresh chart data from current database state
            const freshChartData = await fetchChartData(appointmentId, getState());
            // Determine if chart should be marked as final
            // Priority: 1. Passed chartStatus, 2. Redux state, 3. Default to false
            let isFinal = false;
            if (chartStatus !== undefined) {
                isFinal =
                    chartStatus !== EChartStatus.DRAFT &&
                    chartStatus !== EChartStatus.BACK_TO_PROVIDER;
            } else {
                const currentChart = state.clinicalChartModal.chart;
                isFinal =
                    currentChart?.chartStatus !== EChartStatus.DRAFT &&
                    currentChart?.chartStatus !== EChartStatus.BACK_TO_PROVIDER;
            }
            // Check if chart has signature data for regeneration
            let includeSignature = false;
            let signatureBlob: string | undefined;
            let signer: csl.ISignatureRequestSigner | undefined;
            let signatureText: string | undefined;
            let originalSignedDateTime: string | undefined;
            if (isFinal && freshChartData.signatureRequest?.completedOnISO) {
                includeSignature = true;
                signatureBlob = freshChartData.signatureRequest.signatureBlob;
                signer = {
                    id: freshChartData.signatureRequest.signer.id,
                    firstName: freshChartData.signatureRequest.signer.firstName,
                    lastName: freshChartData.signatureRequest.signer.lastName,
                    position: freshChartData.signatureRequest.signer.position,
                    title: freshChartData.signatureRequest.signer.title,
                };
                signatureText = `${signer.firstName} ${signer.lastName}${signer.position ? ", " + signer.position : ""}`;
                originalSignedDateTime = freshChartData.signatureRequest.completedOnISO;
            }
            // Generate PDF with current database state
            const blob = await generateCompletePDF(clinicalChartPdfComponents, freshChartData, {
                isFinal,
                includeSignature,
                signatureBlob,
                signer,
                signatureText,
                originalSignedDateTime,
            });
            // Create URL from blob for immediate viewing
            const url = URL.createObjectURL(blob);
            // Only upload and update database for finalized/signed charts
            if (isFinal) {
                // Upload the regenerated PDF
                const now = dayjs();
                const fileName = includeSignature
                    ? `chart-regenerated-signed-${now.format(MONTH_DAY_YEAR_TIME_FORMAT)}.pdf`
                    : `chart-regenerated-final-${now.format(MONTH_DAY_YEAR_TIME_FORMAT)}.pdf`;
                const fileUpload = await uploadChartPDF(blob, fileName, accountId.toString());
                if (!fileUpload) {
                    return rejectWithValue("Failed to upload regenerated chart file");
                }
                // Update the chart's file upload reference
                await axios.put(
                    `${BASE_API_URL}/${BASE_SECURE_ROUTE}/staff/appointments/clinical-chart/update-chart-file-upload`,
                    { appointmentId, fileUploadId: fileUpload.id },
                    { withCredentials: true },
                );
                dispatch(triggerSearchTableRefetch([ESearchTableType.CLINICAL_CHARTS_DASHBOARD]));
            }

            return { url };
        } catch (error: unknown) {
            return rejectWithValue(
                error instanceof Error
                    ? error.message
                    : "Failed to regenerate PDF from current state",
            );
        }
    },
);

const clinicalChartModalSlice = createSlice({
    name: "clinicalChartModal",
    initialState: initialStateClinicalChartModal,
    reducers: {
        closeChartModal: (state) => {
            state.isOpen = false;
            state.chart = null;
            if (state.pdfUrl) {
                URL.revokeObjectURL(state.pdfUrl);
            }
            state.pdfUrl = null;
            state.chartData = null;
            state.error = null;
        },
        setLoadingProgress: (state, action: PayloadAction<number>) => {
            state.loadingProgress = action.payload;
        },
        setZoomScale: (state, action: PayloadAction<number>) => {
            state.scale = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(openChartModal.pending, (state, action) => {
                state.isOpen = true;
                state.chart = action.meta.arg.chart;
                state.canUnlock = action.meta.arg.canUnlock;
                state.loadingProgress = 0;
                state.error = null;
                // Set pdfUrl immediately if chart has a signedURL
                if (action.meta.arg.chart.signedURL) {
                    state.pdfUrl = action.meta.arg.chart.signedURL;
                    state.isLoading = false; // No loading needed
                } else {
                    state.pdfUrl = null;
                    state.isLoading = true; // Loading needed for data fetch
                }
            })
            .addCase(openChartModal.fulfilled, (state, action) => {
                // Only set chartData if we actually fetched it
                if (action.payload) {
                    state.chartData = action.payload;
                }
                state.isLoading = false;
            })
            .addCase(openChartModal.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload || "Failed to open chart";
                notify({
                    level: "error",
                    title: "Error",
                    message: state.error,
                });
            })
            .addCase(finalizeChart.pending, (state, action) => {
                state.isLoading = true;
                state.executingActionForAppointmentId = action.meta.arg.appointmentId;
            })
            .addCase(finalizeChart.fulfilled, (state, action) => {
                state.isLoading = false;
                state.executingActionForAppointmentId = undefined;
                if (state.chart) {
                    state.chart.chartStatus = EChartStatus.FINALIZED;
                    state.chart.returnedToProvider = false;
                    // Update file URL properties if present in response
                    if (action.payload?.signedURL) {
                        // Clean up old URL if exists
                        if (state.pdfUrl) {
                            URL.revokeObjectURL(state.pdfUrl);
                        }
                        state.pdfUrl = action.payload.signedURL;
                    }
                }
                notify({
                    level: "success",
                    title: "Success",
                    message: "Chart finalized successfully",
                });
            })
            .addCase(finalizeChart.rejected, (state, action) => {
                state.isLoading = false;
                state.executingActionForAppointmentId = undefined;
                state.error = action.payload || "Failed to finalize chart";
                notify({
                    level: "error",
                    title: "Error",
                    message: state.error,
                });
            })
            .addCase(unlockChart.pending, (state, action) => {
                state.isLoading = true;
                state.executingActionForAppointmentId = action.meta.arg.appointmentId;
            })
            .addCase(unlockChart.fulfilled, (state) => {
                state.isLoading = false;
                state.executingActionForAppointmentId = undefined;
                if (state.chart) {
                    state.chart.chartStatus = EChartStatus.DRAFT;
                }
                // Trigger a PDF refresh
                if (state.pdfUrl) {
                    URL.revokeObjectURL(state.pdfUrl);
                    state.pdfUrl = null;
                }
                notify({
                    level: "success",
                    title: "Success",
                    message: "Chart unlocked successfully",
                });
            })
            .addCase(unlockChart.rejected, (state, action) => {
                state.isLoading = false;
                state.executingActionForAppointmentId = undefined;
                state.error = action.payload || "Failed to unlock chart";
                notify({
                    level: "error",
                    title: "Error",
                    message: state.error,
                });
            })
            .addCase(sendToSupervisor.pending, (state, action) => {
                state.isLoading = true;
                state.executingActionForAppointmentId = action.meta.arg.appointmentId;
            })
            .addCase(sendToSupervisor.fulfilled, (state) => {
                state.isLoading = false;
                state.executingActionForAppointmentId = undefined;
                if (state.chart) {
                    state.chart.returnedToProvider = false;
                }
            })
            .addCase(sendToSupervisor.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload || "Failed to send to supervisor";
            })
            .addCase(downloadChart.pending, (state, action) => {
                state.executingActionForAppointmentId = action.meta.arg.chart.appointmentId;
            })
            .addCase(downloadChart.fulfilled, (state) => {
                state.executingActionForAppointmentId = undefined;
            })
            .addCase(downloadChart.rejected, (state) => {
                state.executingActionForAppointmentId = undefined;
            })
            .addCase(addAddendumAndUpdateChart.pending, (state, action) => {
                state.isLoading = true;
                state.executingActionForAppointmentId = action.meta.arg.appointmentId;
            })
            .addCase(addAddendumAndUpdateChart.fulfilled, (state, _action) => {
                state.isLoading = false;
                state.executingActionForAppointmentId = undefined;
                // Clear the current PDF URL to force regeneration
                if (state.pdfUrl) {
                    URL.revokeObjectURL(state.pdfUrl);
                    state.pdfUrl = null;
                }
                // The chart data will be refetched when the modal is next opened
                state.chartData = null;
            })
            .addCase(addAddendumAndUpdateChart.rejected, (state, action) => {
                state.isLoading = false;
                state.executingActionForAppointmentId = undefined;
                state.error = action.payload || "Failed to add addendum and update chart";
                notify({
                    level: "error",
                    title: "Error",
                    message: state.error,
                });
            })
            .addCase(regeneratePDFFromCurrentState.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(regeneratePDFFromCurrentState.fulfilled, (state, action) => {
                state.isLoading = false;
                // Clean up old URL if exists
                if (state.pdfUrl) {
                    URL.revokeObjectURL(state.pdfUrl);
                }
                state.pdfUrl = action.payload.url;
                // Update the chart data to reflect the new file upload
                // This ensures subsequent operations use the updated chart
                state.chartData = null; // This will trigger a fresh fetch next time if needed
                // Update the chart's lastUpdatedOnUnixUtc to current time and signedURL to the new URL
                if (state.chart) {
                    state.chart.lastUpdatedOnUnixUtc = Date.now();
                    state.chart.signedURL = action.payload.url;
                }
            })
            .addCase(regeneratePDFFromCurrentState.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload || "Failed to regenerate PDF";
            });
    },
});

export const { closeChartModal, setLoadingProgress, setZoomScale } =
    clinicalChartModalSlice.actions;

export default clinicalChartModalSlice.reducer;
